import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { hasNavPath } from "../utils/navPermissions";
import {
  People,
  AccessTime,
  EventNote,
  Business,
  CalendarToday,
  CheckCircle,
} from "@mui/icons-material";

const SkeletonCard = () => (
  <div className="card">
    <div className="flex items-center justify-between mb-4">
      <div className="w-12 h-12 skeleton rounded-2xl" />
      <div className="w-16 h-4 skeleton rounded" />
    </div>
    <div className="w-24 h-8 skeleton rounded mb-2" />
    <div className="w-32 h-4 skeleton rounded" />
  </div>
);

const StatCard = ({
  title,
  value,
  icon: Icon,
  color,
  index,
  onClick,
  subtitle,
}) => {
  const colors = {
    blue: "bg-blue-500",
    green: "bg-green-500",
    orange: "bg-orange-500",
    purple: "bg-purple-500",
  };

  return (
    <motion.div
      className="card group cursor-pointer"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      whileHover={{ y: -8, transition: { duration: 0.2 } }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-4">
        <div
          className={`p-3 ${
            colors[color] || colors.blue
          } rounded-2xl text-white shadow-lg`}
        >
          <Icon className="w-6 h-6" />
        </div>
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          {title}
        </div>
      </div>
      <motion.div
        className="text-4xl font-bold text-primary mb-1"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{
          delay: index * 0.1 + 0.2,
          type: "spring",
          stiffness: 200,
        }}
      >
        {value}
      </motion.div>
      <div className="text-sm text-gray-600">{subtitle || title}</div>
    </motion.div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Check if user is EMPLOYEE (handle both old and new role formats)
  const userRole = user?.role?.toUpperCase() || user?.role;
  const isEmployee = userRole === "EMPLOYEE" || user?.role === "employee";
  const isHr = userRole === "HR" || user?.role === "hr";
  const isEmployeeOrHr = isEmployee || isHr;
  const isAdminOrManager =
    userRole === "ADMIN" || userRole === "MANAGER";
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasExplicitNavPath = (navKey) =>
    Array.isArray(user?.navPermissions) && hasNavPath(user, navKey);
  const canSeeEmployeesCard = hasExplicitNavPath("/employees");
  const canSeeHolidaysCard = hasExplicitNavPath("/holidays");

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        // Simulate API delay for skeleton effect
        await new Promise((resolve) => setTimeout(resolve, 800));

        const response = await api.get("/dashboard/stats");
        const data = response.data;

        console.log("📊 Dashboard stats received:", {
          isEmployeeOrHr,
          rawData: data,
          totalUsers: data.totalUsers,
          canSeeEmployeesCard,
          canSeeHolidaysCard,
        });
        let upcomingHoliday = null;
        if (isEmployeeOrHr && canSeeHolidaysCard) {
          try {
            const holidaysResponse = await api.get("/holidays");
            const holidayList = Array.isArray(holidaysResponse.data)
              ? holidaysResponse.data
              : Array.isArray(holidaysResponse.data?.data)
              ? holidaysResponse.data.data
              : [];
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            upcomingHoliday = holidayList.find((holiday) => {
              const holidayDate = new Date(holiday?.date);
              if (Number.isNaN(holidayDate.getTime())) return false;
              holidayDate.setHours(0, 0, 0, 0);
              return holidayDate >= today;
            });
          } catch (holidayErr) {
            console.error("Failed to fetch holidays for dashboard", holidayErr);
          }
        }

        const updatedStats = isEmployeeOrHr
          ? [
              ...(canSeeEmployeesCard
                ? [
                    {
                      title: "Total Users",
                      value: data.totalUsers ?? data.totalEmployees ?? 0,
                      icon: People,
                      color: "blue",
                      path: "/employees",
                    },
                  ]
                : []),
              ...(canSeeHolidaysCard
                ? [
                    {
                      title: "Next Holiday",
                      value: upcomingHoliday?.name || "No Upcoming",
                      subtitle: upcomingHoliday?.date
                        ? new Date(upcomingHoliday.date).toLocaleDateString()
                        : "No upcoming holidays",
                      icon: CalendarToday,
                      color: "purple",
                      path: "/holidays",
                    },
                  ]
                : []),
            ]
          : [
              {
                title: "Total Users",
                value: data.totalUsers ?? data.totalEmployees ?? 0,
                icon: People,
                color: "blue",
                path: "/employees",
              },
              {
                title: "Today Attendance",
                value: data.todayAttendance ?? 0,
                icon: AccessTime,
                color: "green",
                path: "/attendance/report",
              },
              {
                title: "Pending Leaves",
                value: data.pendingLeaves ?? 0,
                icon: EventNote,
                color: "orange",
                path: "/leave-approval",
              },
              {
                title: "Departments",
                value: data.totalDepartments ?? 0,
                icon: Business,
                color: "purple",
                path: "/departments",
              },
            ];

        console.log("✅ Setting dashboard stats:", updatedStats);
        setStats(updatedStats);
      } catch (err) {
        console.error("Failed to fetch dashboard stats", err);
        setStats(
          isEmployeeOrHr
            ? []
            : [
                {
                  title: "Total Users",
                  value: "0",
                  icon: People,
                  color: "blue",
                  path: "/employees",
                },
                {
                  title: "Today Attendance",
                  value: "0",
                  icon: AccessTime,
                  color: "green",
                  path: "/attendance/report",
                },
                {
                  title: "Pending Leaves",
                  value: "0",
                  icon: EventNote,
                  color: "orange",
                  path: "/leave-approval",
                },
                {
                  title: "Departments",
                  value: "0",
                  icon: Business,
                  color: "purple",
                  path: "/departments",
                },
              ]
        );
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchStats();
    }
  }, [
    isEmployeeOrHr,
    canSeeEmployeesCard,
    canSeeHolidaysCard,
    user,
    location.pathname,
  ]); // Refresh when location changes

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-6 sm:mb-8"
      >
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary mb-2">
          Welcome back, {user?.name?.split(" ")[0]}! 👋
        </h1>
        <p className="text-sm sm:text-base lg:text-lg text-gray-600">
          Here's what's happening with your{" "}
          {isAdminOrManager ? "organization" : "account"} today.
        </p>
      </motion.div>

      {/* Stats Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {Array.from({ length: isEmployeeOrHr ? 2 : 4 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : (
        <>
          {stats.length === 0 ? (
            <div className="card text-center py-10">
              <p className="text-gray-600">
                No dashboard widgets assigned yet. Ask admin to grant access in
                Nav Permissions.
              </p>
            </div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className={`grid grid-cols-1 sm:grid-cols-2 ${
                isEmployeeOrHr ? "lg:grid-cols-2" : "lg:grid-cols-4"
              } gap-4 sm:gap-6`}
            >
              {stats.map((stat, index) => (
                <StatCard
                  key={stat.title}
                  {...stat}
                  index={index}
                  onClick={() => stat.path && navigate(stat.path)}
                />
              ))}
            </motion.div>
          )}
        </>
      )}

      {/* Analytics Link for Admin/Manager only */}
      {isAdminOrManager && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="mt-8"
        >
          <div
            className="card bg-gradient-to-r from-primary to-secondary text-white p-6 cursor-pointer hover:shadow-glow transition-all"
            onClick={() => navigate("/dashboard/analytics")}
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold mb-2">
                  View Analytics Dashboard
                </h3>
                <p className="text-white/90">
                  Comprehensive HR insights, charts, and metrics
                </p>
              </div>
              <div className="text-4xl">📊</div>
            </div>
          </div>
        </motion.div>
      )}

      {!isEmployeeOrHr && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="mt-12"
        >
          <h2 className="text-2xl font-bold text-primary mb-6">Quick Actions</h2>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="card">
                  <div className="w-12 h-12 bg-gray-200 rounded-2xl mb-4 animate-pulse"></div>
                  <div className="h-6 bg-gray-200 rounded-lg mb-2 animate-pulse w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-1/2"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  title: "Mark Attendance",
                  description: "Check in or check out",
                  path: "/attendance",
                  color: "bg-green-500",
                  icon: AccessTime,
                },
                {
                  title: "Request Leave",
                  description: "Submit a new leave request",
                  path: "/leave",
                  color: "bg-blue-500",
                  icon: EventNote,
                },
                {
                  title: "View Holidays",
                  description: "Check upcoming holidays",
                  path: "/holidays",
                  color: "bg-purple-500",
                  icon: CalendarToday,
                },
              ].map((action, index) => {
                const ActionIcon = action.icon;
                return (
                  <motion.div
                    key={action.title}
                    className="card group cursor-pointer"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.6 + index * 0.1 }}
                    whileHover={{ scale: 1.02, y: -4 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate(action.path)}
                  >
                    <div
                      className={`w-12 h-12 ${action.color} rounded-2xl mb-4 flex items-center justify-center text-white shadow-lg`}
                    >
                      <ActionIcon className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold text-primary mb-2">
                      {action.title}
                    </h3>
                    <p className="text-gray-600">{action.description}</p>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
